/**
 * The forgiving reader behind paste and drop: it takes a whole date or time in any common form and
 * returns its parts, which the field then shows in its own segments. Text with more in it than a
 * date or time is not read. Typing never goes through it; the segments take keys one at a time.
 */

/** A calendar day. `month` counts from 0, as `Date` does. */
export interface SbDateParts {
  year: number;
  month: number;
  day: number;
}

/** A wall-clock time, 24h. */
export interface SbTimeParts {
  hour: number;
  minute: number;
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

export const daysInMonth = (year: number, month: number): number => new Date(year, month + 1, 0).getDate();

/** Parts for a real calendar day; null for 02/31, month 13 and the like. */
export const realDay = (year: number, month: number, day: number): SbDateParts | null => {
  if (!Number.isInteger(year) || year < 1000 || year > 9999 || month < 0 || month > 11) {
    return null;
  }
  return day >= 1 && day <= daysInMonth(year, month) ? { year, month, day } : null;
};

/**
 * A two-digit year is the one ending in those digits from 80 years before this year to 19 after
 * (ICU's window): in 2026, 26 is 2026, 45 is 2045 and 99 is 1999.
 */
const fullYear = (text: string): number => {
  if (text.length !== 2) {
    return Number(text);
  }
  const start = new Date().getFullYear() - 80;
  return start + ((Number(text) - (start % 100) + 100) % 100);
};

const monthFromName = (text: string): number => MONTHS.indexOf(text.slice(0, 3).toLowerCase());

/** A weekday in front of a date, as the long form says it (`Wednesday, October 28, 2026`). */
const WEEKDAY_RE = /^(sun|mon|tue|wed|thu|fri|sat)[a-z]*\.?,?\s+/i;

const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** A date in one of the forms `parseLooseDate` reads, with no weekday in front. */
const parseDate = (text: string): SbDateParts | null => {
  if (!text) {
    return null;
  }
  let m = text.match(/^(\d{4})([-/.])(\d{1,2})\2(\d{1,2})$/);
  if (m) {
    return realDay(Number(m[1]), Number(m[3]) - 1, Number(m[4]));
  }
  m = text.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4}|\d{2})$/);
  if (m) {
    return realDay(fullYear(m[3]), Number(m[2]) - 1, Number(m[1]));
  }
  m = text.match(/^(\d{1,2})[/\-\s]+(\d{1,2})[/\-\s]+(\d{4}|\d{2})$/);
  if (m) {
    return realDay(fullYear(m[3]), Number(m[1]) - 1, Number(m[2]));
  }
  m = text.match(/^(\d{8})$/);
  if (m) {
    const digits = m[1];
    const iso = realDay(Number(digits.slice(0, 4)), Number(digits.slice(4, 6)) - 1, Number(digits.slice(6, 8)));
    return iso || realDay(Number(digits.slice(4, 8)), Number(digits.slice(0, 2)) - 1, Number(digits.slice(2, 4)));
  }
  m = text.match(/^(\d{1,2})(?:st|nd|rd|th)?[\s.\-]+([A-Za-z]{3,})\.?,?[\s.\-]+(\d{4})$/);
  if (m && monthFromName(m[2]) >= 0) {
    return realDay(Number(m[3]), monthFromName(m[2]), Number(m[1]));
  }
  m = text.match(/^([A-Za-z]{3,})\.?[\s.\-]+(\d{1,2})(?:st|nd|rd|th)?,?[\s.\-]+(\d{4})$/);
  if (m && monthFromName(m[1]) >= 0) {
    return realDay(Number(m[3]), monthFromName(m[1]), Number(m[2]));
  }
  return null;
};

/**
 * `2026-10-28` (also `2026/10/28` and `2026.10.28`), `10/28/2026` with `/ - space` separators
 * (two-digit years by `fullYear`), `28.10.2026` (dots are day first), `20261028`, `10282026`,
 * `28 Oct 2026`, `Oct 28, 2026`, and any of those after a weekday (`Wednesday, October 28, 2026`,
 * what the field itself announces). A weekday that is not the date's makes it unreadable: one of the
 * two is wrong. A date alone: with a time after it, `parseLooseDateTime` reads it.
 */
export const parseLooseDate = (input: string): SbDateParts | null => {
  const text = (input || '').trim();
  const weekday = text.match(WEEKDAY_RE);
  const date = parseDate(text.replace(WEEKDAY_RE, ''));
  return date && weekday && new Date(date.year, date.month, date.day).getDay() !== WEEKDAYS.indexOf(weekday[1].toLowerCase()) ? null : date;
};

/**
 * The 24-hour hour for an hour of 1-12 given without AM or PM: 7-11 are morning, 1-6 and 12
 * afternoon, the hours people mean. Typed text and the hour column both follow it.
 */
export const likelyHour = (hour12: number): number => (hour12 >= 7 && hour12 <= 11 ? hour12 : (hour12 % 12) + 12);

/**
 * `9:30pm`, `9.30 p`, `9pm`, `21:30`, `2130`, `0930`, `930` (three digits are H:MM), `15:30:00`
 * (seconds dropped), `09:30 AM`. With no AM or PM, a leading zero, 0 or 13-23 is 24-hour time
 * (`01:30` is 1:30 AM) and any other hour follows `likelyHour` (`1:30` is 1:30 PM).
 */
export const parseLooseTime = (input: string): SbTimeParts | null => {
  const text = (input || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const m = text.match(/^(\d{1,2})(?:[:.]?(\d{2}))?(?:[:.]\d{2}(?:\.\d+)?)?\s?([ap])?\.?(?:m\.?)?$/);
  if (!m) {
    return null;
  }
  // "930" backtracks to 9 + 30, so three digits read as H:MM.
  const minuteText = m[2];
  let hour = Number(m[1]);
  const minute = minuteText === undefined ? 0 : Number(minuteText);
  const period = m[3];
  if (!period && minuteText === undefined) {
    // A bare "9" is too little to be a time.
    return null;
  }
  if (period) {
    if (hour < 1 || hour > 12) {
      return null;
    }
    hour = (hour % 12) + (period === 'p' ? 12 : 0);
  } else if (hour >= 1 && hour <= 12 && !m[1].startsWith('0')) {
    hour = likelyHour(hour);
  }
  if (hour > 23 || minute > 59) {
    return null;
  }
  return { hour, minute };
};

/**
 * A date and a time with `T`, a space or a comma between them; either part may be missing. A time
 * with a zone (`Z`, `+01:00`) names an instant, which a field of wall times could only take by
 * dropping the zone or converting it, each another time than the text says: it is not read.
 */
export const parseLooseDateTime = (input: string): { date: SbDateParts | null; time: SbTimeParts | null } | null => {
  const text = (input || '').trim();
  if (!text) {
    return null;
  }
  // A time after a year-first date has its colon, dot or AM/PM: `2026-10-28 2026` is no time.
  const iso = text.match(/^(\d{4}([-/.])\d{1,2}\2\d{1,2})[T\s]+(\d{1,2}[:.]\d{2}.*|\d{1,2}\s?[ap].*)$/i);
  if (iso) {
    const date = parseLooseDate(iso[1]);
    const time = parseLooseTime(iso[3]);
    return date && time ? { date, time } : null;
  }
  const date = parseLooseDate(text);
  if (date) {
    return { date, time: null };
  }
  const time = parseLooseTime(text);
  if (time) {
    return { date: null, time };
  }
  // Split at the last comma, or at the space before the time's first digit run.
  const split = text.match(/^(.*\d{2,4})\s*(?:,|\s+(?:at\s+)?)\s*(\d{1,2}(?:[:.]\d{2})?(?:[:.]\d{2})?\s*(?:[ap]\.?m?\.?)?)$/i);
  if (split) {
    const splitDate = parseLooseDate(split[1].replace(/,$/, ''));
    const splitTime = parseLooseTime(split[2]);
    if (splitDate && splitTime) {
      return { date: splitDate, time: splitTime };
    }
  }
  return null;
};
